/**
 * JSON bodies for APIs backed by strict DTOs / Elasticsearch. Values from HTML inputs or hand-edited
 * JSON can be strings (including EU-style "40,7128" decimals, or "100,50" for prices); this module
 * deep-walks and coerces known numeric fields. Used for asset create/update and for register POSTs.
 */

import { DEFAULT_ASSET_LATITUDE, DEFAULT_ASSET_LONGITUDE } from '../constants/assetDefaultGeo';
import { buildAddressGeocodeQuery } from '../utils/assetGeo';
import { applyImplicitMinCapacityForAssetMutation } from '../utils/assetCreateCapacity';
import { assetCreateShowsRegionField } from '../utils/assetCreateFormDefaults';
import { coerceImagesWireValue, extractImageUrlStrings } from '../utils/assetImageResolve';
import { isPhoneWireFieldKey, normalizePhoneForWire } from '../utils/phoneE164';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) {
    return false;
  }
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

const ASSET_NUMERIC_KEYS = new Set([
  'latitude',
  'longitude',
  'state_id',
  'country_id',
  'role_id',
  'asset_type_id',
  'min_capacity',
  'max_capacity',
  'pricing_type_id',
  'price',
  'addon_id',
  'addon_price',
  'available_quantity',
]);

const GEO_KEYS = new Set(['latitude', 'longitude']);

/**
 * Strips `undefined` for JSON / IE plain clone; `latitude: "x"` is preserved for the walk to fix.
 */
function plainJsonClone<T>(v: T): T {
  try {
    return JSON.parse(JSON.stringify(v)) as T;
  } catch {
    return v;
  }
}

/**
 * Parses a user-entered numeric string. Handles "40,7128" (EU decimal) and "1,234" (thousands)
 * when unambiguous: one comma, three fraction digits, one digit in the int part => thousands.
 */
function parseNumericString(tRaw: string): number | undefined {
  const t = tRaw.trim();
  if (t === '' || t === 'null' || t === 'undefined') {
    return undefined;
  }
  let n = Number(t);
  if (Number.isFinite(n)) {
    return n;
  }
  if (t.includes(',') && !t.includes('.')) {
    const m = t.match(/^([-+]?)(\d+),(\d+)$/);
    if (m) {
      const sign = m[1];
      const intPart = m[2];
      const frac = m[3];
      if (intPart.length === 1 && frac.length === 3) {
        n = Number(sign + intPart + frac);
        if (Number.isFinite(n)) {
          return n;
        }
      }
      n = Number(sign + intPart + '.' + frac);
      if (Number.isFinite(n)) {
        return n;
      }
    }
  }
  if (t.includes(',')) {
    n = Number(t.replace(/,/g, ''));
    if (Number.isFinite(n)) {
      return n;
    }
  }
  if (t.includes(',') && !t.includes('.')) {
    n = Number(t.replace(/,/g, '.'));
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return undefined;
}

/**
 * Coerces a `latitude` / `longitude` value to a **primitive IEEE-754 number** (JavaScript `number`;
 * JSON has no separate float type). Used at walk + stringify so wire JSON never leaves quoted strings
 * for geo fields.
 */
function coerceSingleGeoToPrimitive(v: unknown): number | undefined {
  if (v === null || v === undefined) {
    return undefined;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    return parseNumericString(v);
  }
  if (typeof v === 'object' && v !== null) {
    if (v instanceof Number) {
      const n = v.valueOf();
      return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
    }
    const v0 = (v as { valueOf?: () => unknown }).valueOf;
    if (typeof v0 === 'function') {
      const p = (v as { valueOf: () => unknown }).valueOf();
      if (typeof p === 'number' && Number.isFinite(p)) {
        return p;
      }
      if (typeof p === 'string') {
        return parseNumericString(p);
      }
    }
  }
  return undefined;
}

function assetGeoKeyJsonReplacer(key: string, value: unknown): unknown {
  if (key === 'latitude' || key === 'longitude') {
    const n = coerceSingleGeoToPrimitive(value);
    return n === undefined ? undefined : n;
  }
  return value;
}

function coerceNonGeoNumericLeaf(v: unknown): unknown {
  if (v === null || v === undefined) {
    return v;
  }
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : null;
  }
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || t === 'null' || t === 'undefined') {
      return null;
    }
    const n = parseNumericString(t);
    if (n !== undefined) {
      return n;
    }
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : v;
}

/**
 * API `IsValidOpeningTimes`: each value must be the literal string `"closed"` or `{ open: "hh:mm", close: "hh:mm" }`.
 * Rows from the API/Java may deserialize as `{ closed: true }`; Elasticsearch maps `opening_times.<day>` as **text**, so plain
 * objects besides `{ open, close }` must not be sent. Empty `{ open: "", close: "" }` fails validation; coerce to `"closed"`.
 */
function tryParseOpeningTimesJsonString(s: string): unknown | null {
  const t = s.trim();
  if (!t.startsWith('{')) {
    return null;
  }
  try {
    return JSON.parse(t) as unknown;
  } catch {
    return null;
  }
}

function normalizeOpeningTimesValue(val: unknown): unknown {
  if (val == null) {
    return 'closed';
  }
  if (val === 'closed' || val === '') {
    return 'closed';
  }
  if (typeof val === 'string') {
    const t = val.trim();
    if (t.toLowerCase() === 'closed') {
      return 'closed';
    }
    const parsed = tryParseOpeningTimesJsonString(t);
    if (parsed !== null) {
      return normalizeOpeningTimesValue(parsed);
    }
    return val;
  }
  if (!isPlainObject(val)) {
    return val;
  }

  const o = val as Record<string, unknown>;

  if ('open' in o && 'close' in o) {
    const openS = o.open == null ? '' : String(o.open).trim();
    const closeS = o.close == null ? '' : String(o.close).trim();
    if (openS === '' && closeS === '') {
      return 'closed';
    }
    if (openS === '' || closeS === '') {
      return 'closed';
    }
    return { open: openS, close: closeS };
  }

  // e.g. Jackson `{ closed: true }` previewed by ES as `{closed=true}`
  if ('closed' in o) {
    return 'closed';
  }

  // Unknown object shape breaks ES text mapping and fails DTO
  return 'closed';
}

function normalizeOpeningTimesObject(v: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(v)) {
    out[key] = normalizeOpeningTimesValue(v[key]);
  }
  return out;
}

/**
 * Root `opening_times` only: turns API/Jackson `{ closed: true }` per weekday into the string `"closed"`.
 * Keeps React body state aligned with wire JSON (same shape as `prepareAssetRequestBody` for this block).
 */
export function coerceAssetBodyOpeningTimes(body: Record<string, unknown>): Record<string, unknown> {
  const ot = body.opening_times;
  if (!isPlainObject(ot)) {
    return body;
  }
  return {
    ...body,
    opening_times: normalizeOpeningTimesObject({ ...(ot as Record<string, unknown>) }),
  };
}

/**
 * Parse search/list/GET quirks (stringified blob or ES text per-day JSON) for {@link AssetGetByIdView}.
 */
export function normalizeOpeningTimesForDisplay(v: unknown): Record<string, unknown> | null {
  const coerced = coerceRootOpeningTimesValue(v);
  if (!isPlainObject(coerced)) {
    return null;
  }
  const keys = Object.keys(coerced);
  return keys.length > 0 ? (coerced as Record<string, unknown>) : null;
}

const OPENING_TIMES_DISPLAY_ALIASES = [
  'opening_times',
  'opening_times_json',
  'openingTimesJson',
] as const;

/**
 * Resolve opening hours for display from asset records.
 * Search index entries for newer assets often expose `opening_times_json` (string) instead of `opening_times`.
 */
export function resolveAssetOpeningTimesForDisplay(
  record: Record<string, unknown>,
): Record<string, unknown> | null {
  const tryRecord = (row: Record<string, unknown>): Record<string, unknown> | null => {
    for (const key of OPENING_TIMES_DISPLAY_ALIASES) {
      if (!(key in row)) {
        continue;
      }
      const parsed = normalizeOpeningTimesForDisplay(row[key]);
      if (parsed) {
        return parsed;
      }
    }
    return null;
  };

  const fromTop = tryRecord(record);
  if (fromTop) {
    return fromTop;
  }

  for (const nestedKey of ['asset', 'facility', 'Asset'] as const) {
    const nested = record[nestedKey];
    if (!isPlainObject(nested)) {
      continue;
    }
    const fromNested = tryRecord(nested);
    if (fromNested) {
      return fromNested;
    }
  }

  return null;
}

/** Hand-edit / backend quirks: `opening_times` may arrive as a JSON string instead of an object. */
function coerceRootOpeningTimesValue(v: unknown): unknown {
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || t === 'null') {
      return {};
    }
    try {
      const parsed: unknown = JSON.parse(t);
      if (isPlainObject(parsed)) {
        return normalizeOpeningTimesObject(parsed as Record<string, unknown>);
      }
    } catch {
      return v;
    }
    return v;
  }
  if (isPlainObject(v)) {
    return normalizeOpeningTimesObject(v as Record<string, unknown>);
  }
  return v;
}

/**
 * Second pass: pick up `opening_times` at any depth (e.g. version-prefixed routes used to skip prepare,
 * or nested payloads) and normalize stringified blobs.
 */
function deepNormalizeOpeningTimesBranches(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(deepNormalizeOpeningTimesBranches);
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const o = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o)) {
    const child = o[k];
    if (k === 'opening_times') {
      out[k] = coerceRootOpeningTimesValue(child);
    } else {
      out[k] = deepNormalizeOpeningTimesBranches(child);
    }
  }
  return out;
}

/**
 * The API DTO allows `latitude` / `longitude` on the **root** only; nested `address.latitude` and
 * `address.longitude` are rejected (`forbidNonWhitelisted` / @Allow). Strip them if hand-edited JSON
 * or older samples included them, so create/update does not 400.
 */
function stripDisallowedAddressGeoKeys(out: Record<string, unknown>): void {
  const addr = out.address;
  if (!isPlainObject(addr)) {
    return;
  }
  if (!('latitude' in addr) && !('longitude' in addr)) {
    return;
  }
  const a = { ...addr } as Record<string, unknown>;
  delete a.latitude;
  delete a.longitude;
  out.address = a;
}

/**
 * Only asset create/update entity bodies — not `POST /search/assets` or other routes under `/assets/…`.
 */
export function shouldPrepareAssetEntityRequestBody(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
): boolean {
  const p = path.replace(/\\/g, '/').toLowerCase();
  if (method === 'POST') {
    return /\/assets\/create\/?$/.test(p);
  }
  if (method === 'PATCH') {
    return /^\/assets\/[^/]+\/?$/.test(p);
  }
  return false;
}

/**
 * Deep clone (via JSON) then recursively coerce numbers. `latitude` / `longitude` are set only
 * when a finite number results; **strings are never left** in those keys.
 */
export function prepareAssetRequestBody(input: unknown): unknown {
  const root = input !== null && typeof input === 'object' && !Array.isArray(input) ? plainJsonClone(input) : input;
  if (root === null || root === undefined) {
    return root;
  }
  const walked = walkBody(root, null);
  if (walked === null || !isPlainObject(walked)) {
    return walked;
  }
  stripDisallowedAddressGeoKeys(walked);
  const normalized = deepNormalizeOpeningTimesBranches(walked);
  if (isPlainObject(normalized)) {
    const lat = coerceSingleGeoToPrimitive(normalized.latitude);
    const lng = coerceSingleGeoToPrimitive(normalized.longitude);
    const canGeocodeFromAddress = buildAddressGeocodeQuery(normalized) !== null;
    if (lat !== undefined && lng !== undefined) {
      normalized.latitude = lat;
      normalized.longitude = lng;
    } else if (!canGeocodeFromAddress) {
      normalized.latitude = DEFAULT_ASSET_LATITUDE;
      normalized.longitude = DEFAULT_ASSET_LONGITUDE;
    } else {
      delete normalized.latitude;
      delete normalized.longitude;
    }
    delete normalized.addons;
    const withMinCapacity = applyImplicitMinCapacityForAssetMutation(normalized);
    normalized.min_capacity = withMinCapacity.min_capacity;
  }
  return normalized;
}

/**
 * Register POST bodies (`/register/*`): coerce numeric fields but never inject root `latitude` /
 * `longitude` — those keys are asset-only and the register DTO rejects them.
 */
export function prepareRegisterRequestBody(input: unknown): unknown {
  const root = input !== null && typeof input === 'object' && !Array.isArray(input) ? plainJsonClone(input) : input;
  if (root === null || root === undefined) {
    return root;
  }
  const walked = walkBody(root, null);
  if (walked === null || !isPlainObject(walked)) {
    return walked;
  }
  stripDisallowedAddressGeoKeys(walked);
  delete walked.latitude;
  delete walked.longitude;
  return walked;
}

/**
 * at any depth so the wire body uses JSON numbers, not stringified strings or `Number` object
 * shells. If the error still says `parse_exception: latitude must be a number` while DevTools
 * shows unquoted `latitude` in the request JSON, fix the **API / Elasticsearch** ingest or
 * document mapping, not the browser.
 */
export function stringifyPreparedAssetForHttp(prepared: unknown): string {
  const s = JSON.stringify(prepared, assetGeoKeyJsonReplacer);
  return s === undefined ? 'null' : s;
}

/**
 * Root JSON keys PATCH `/assets/:id` forbids (@Allow whitelist) **or** the service ignores for reassignment.
 * Strip these before PATCH; expose for read-only UX on update forms.
 */
export const ASSET_PATCH_OMITTED_ROOT_KEYS = new Set<string>([
  'id',
  'created_at',
  'updated_at',
  'deleted_at',
  'asset_id',
  // UpdateAssetDto / whitelist reject from GET payloads
  'avg_rating',
  'reviews_count',
  'state_name',
  'country_name',
  'owner_id',
  'user_id',
  'asset_type_name',
  'location',
  // Ignored by updateAsset — omit so users do not think they persist
  'country_id',
  'registration_date',
  // Blackout dates are edited under opening_times (ISO date keys), not this legacy id list.
  'blackout_ranges',
]);

/** Roots hidden from the asset update editor (not sent on PATCH or shown as URL/text fields). */
export const ASSET_UPDATE_EDITOR_HIDDEN_ROOT_KEYS = new Set<string>([
  ...Array.from(ASSET_PATCH_OMITTED_ROOT_KEYS),
  // Owned via booking-availability toggle (still sent on PATCH when changed).
  'asset_status',
  'images',
  'email',
  'phone_number',
  'phone',
  'mobile',
  'latitude',
  'longitude',
  'owner',
  'asset_owner',
  'assetOwner',
  'private_asset_owner',
  'business_asset_owner',
]);

export function stripAssetUpdateEditorFormData(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...body };
  ASSET_UPDATE_EDITOR_HIDDEN_ROOT_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

function coerceAssetImagesToWireStringArray(images: unknown): string[] | null {
  if (!Array.isArray(images) && typeof images !== 'string') {
    return null;
  }
  return extractImageUrlStrings(coerceImagesWireValue(images));
}

/** Coerce GET-style `images[]` (`string` | `{ url, … }` | JSON string) to wire URLs/paths for forms and PATCH. */
export function normalizeAssetImagesToWireStrings(images: unknown): string[] {
  return coerceAssetImagesToWireStringArray(images) ?? [];
}

function isTruthyAssetTypeIdWire(v: unknown): boolean {
  if (v === null || v === undefined || v === '') {
    return false;
  }
  if (typeof v === 'number') {
    return Number.isFinite(v) && v > 0;
  }
  if (typeof v === 'string') {
    const n = Number(v.trim());
    return Number.isFinite(n) && n > 0;
  }
  return false;
}

/**
 * Update/create bodies must send `facilities` as `number[]` (facility ids). GET payloads often use
 * `{ facility_id, name, … }` rows; {@link walkBody} would leave objects on the wire and some APIs
 * ignore or reject them, so PATCH always replaces with a clean id list when the editor included `facilities`.
 */
function coerceFacilitiesRootToIdArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: number[] = [];
  for (const x of value) {
    if (typeof x === 'number' && Number.isFinite(x)) {
      out.push(x);
      continue;
    }
    if (typeof x === 'string' && x.trim()) {
      const n = Number(x.trim());
      if (Number.isFinite(n)) {
        out.push(n);
      }
      continue;
    }
    if (isPlainObject(x)) {
      const row = x as Record<string, unknown>;
      const raw = row.facility_id ?? row.id ?? row.Id ?? row.facilityId ?? row.FacilityId;
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        out.push(raw);
      } else if (typeof raw === 'string' && raw.trim()) {
        const n = Number(raw.trim());
        if (Number.isFinite(n)) {
          out.push(n);
        }
      }
    }
  }
  return out;
}

function coercePatchCountryId(value: unknown): unknown {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

export type SanitizeAssetPatchRequestBodyOptions = {
  /**
   * When PATCH is sent as `multipart/form-data` with `images` file parts, the JSON field may need
   * an explicit `images: []` so the server pairs file bytes with “no URL-only entries”. JSON-only
   * PATCH should omit `images` when there are no wire URLs to send.
   */
  preserveEmptyWireImagesForMultipartFiles?: boolean;
  /** Asset country — used to omit `state_id` for non-US assets on PATCH. */
  countryId?: unknown;
};

/**
 * Omit keys the PATCH handler forbids or never applies; coerce `images` to `string[]` (URLs/paths).
 * Matches UpdateAssetDto + updateAsset semantics (truthy asset_type_id only applies when sent).
 */
function patchWireJsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * PATCH only fields that differ from the GET snapshot used to open Edit — keeps JSON under ~100 KB limits
 * many upstream stacks enforce (full asset bodies with image URLs often exceed that).
 */
export function buildAssetPatchDelta(
  baseline: Record<string, unknown> | null | undefined,
  draft: Record<string, unknown>,
): Record<string, unknown> {
  const draftImages =
    isPlainObject(draft) && Object.prototype.hasOwnProperty.call(draft, 'images')
      ? coerceAssetImagesToWireStringArray(draft.images) ?? []
      : null;
  const baseImages =
    baseline && isPlainObject(baseline) && Object.prototype.hasOwnProperty.call(baseline, 'images')
      ? coerceAssetImagesToWireStringArray(baseline.images) ?? []
      : null;

  const patchCountryId = coercePatchCountryId(
    (baseline && isPlainObject(baseline) ? baseline.country_id : undefined) ?? draft.country_id,
  );

  const draftSanitized = sanitizeAssetPatchRequestBody(draft, { countryId: patchCountryId });
  if (!baseline || !isPlainObject(baseline)) {
    if (draftImages !== null && draftImages.length === 0) {
      return { ...draftSanitized, images: [] };
    }
    return draftSanitized;
  }
  const baseSanitized = sanitizeAssetPatchRequestBody(baseline, { countryId: patchCountryId });
  const delta: Record<string, unknown> = {};
  for (const key of Object.keys(draftSanitized)) {
    const cur = draftSanitized[key];
    if (patchWireJsonEqual(cur, baseSanitized[key])) {
      continue;
    }
    delta[key] = cur;
  }
  if (draftImages !== null && baseImages !== null && !patchWireJsonEqual(draftImages, baseImages)) {
    delta.images = draftImages;
  }
  if (
    Object.prototype.hasOwnProperty.call(delta, 'pricing_methods') &&
    Array.isArray(delta.pricing_methods) &&
    delta.pricing_methods.length === 0
  ) {
    delete delta.pricing_methods;
  }
  return delta;
}

/** UTF-8 byte length of the wire JSON PATCH body (for size-limit hints). */
export function estimateAssetPatchBodyBytes(body: Record<string, unknown>): number {
  const s = stringifyPreparedAssetForHttp(body);
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(s).length;
  }
  return s.length;
}

export function sanitizeAssetPatchRequestBody(
  input: unknown,
  opts?: SanitizeAssetPatchRequestBodyOptions,
): Record<string, unknown> {
  const prepared = prepareAssetRequestBody(input);
  if (prepared === null || typeof prepared !== 'object' || Array.isArray(prepared)) {
    return {};
  }
  const o = { ...(prepared as Record<string, unknown>) };
  const inputRecord = isPlainObject(input) ? (input as Record<string, unknown>) : null;
  const patchCountryId = coercePatchCountryId(opts?.countryId ?? inputRecord?.country_id);
  ASSET_PATCH_OMITTED_ROOT_KEYS.forEach((k) => {
    delete o[k];
  });
  if ('images' in o) {
    const coerced = coerceAssetImagesToWireStringArray(o.images);
    if (coerced !== null && coerced.length > 0) {
      o.images = coerced;
    } else if (opts?.preserveEmptyWireImagesForMultipartFiles && coerced !== null) {
      o.images = [];
    } else {
      delete o.images;
    }
  }
  // UpdateAssetDto requires @ArrayMinSize(1) when pricing_methods is present — omit when not editing pricing.
  if ('pricing_methods' in o) {
    const methods = o.pricing_methods;
    if (!Array.isArray(methods) || methods.length === 0) {
      delete o.pricing_methods;
    }
  }
  if ('asset_type_id' in o && !isTruthyAssetTypeIdWire(o.asset_type_id)) {
    delete o.asset_type_id;
  }
  const facilitiesFromInput = inputRecord && 'facilities' in inputRecord ? inputRecord.facilities : undefined;
  const facilitiesSource = facilitiesFromInput !== undefined ? facilitiesFromInput : o.facilities;
  if (facilitiesSource !== undefined) {
    const ids = coerceFacilitiesRootToIdArray(facilitiesSource);
    o.facilities = ids;
    // Some PATCH DTOs mirror pricing_methods and only read snake_case `facility_ids`.
    o.facility_ids = ids;
    o.facilityIds = ids;
  }
  // Asset create/update UI no longer edits add-ons; promoted items live under facilities.
  delete o.addons;
  if (!assetCreateShowsRegionField(patchCountryId)) {
    delete o.state_id;
  }
  return o;
}

/** Roots that must not send on PATCH → treat subtree as display-only during update edits. */
export function isAssetPatchReadonlyWirePath(segments: readonly string[]): boolean {
  return segments.length >= 1 && ASSET_PATCH_OMITTED_ROOT_KEYS.has(String(segments[0]));
}

/** Update editor: PATCH-omitted roots only (address is editable). */
export function isAssetUpdateEditorReadonlyWirePath(segments: readonly string[]): boolean {
  return isAssetPatchReadonlyWirePath(segments);
}

function pickTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Ensure nested address has editor keys (zip display) when loading GET payloads for update. */
export function ensureAddressEditorShape(body: Record<string, unknown>): Record<string, unknown> {
  const addr = body.address;
  if (!isPlainObject(addr)) {
    return body;
  }
  const nextAddr = { ...addr };
  if (!('zip_code' in nextAddr)) {
    const zip =
      pickTrimmedString(nextAddr.postal_code) ||
      pickTrimmedString(body.zip_code) ||
      pickTrimmedString(body.postal_code);
    nextAddr.zip_code = zip;
  }
  if (!('address2' in nextAddr)) {
    nextAddr.address2 = pickTrimmedString(nextAddr.address2);
  }
  return { ...body, address: nextAddr };
}

function walkBody(value: unknown, _parentKey: string | null): unknown {
  if (value === null) {
    return null;
  }
  if (Array.isArray(value)) {
    if (_parentKey === 'facilities') {
      return value.map((x) => {
        if (typeof x === 'number' && !Number.isNaN(x) && Number.isFinite(x)) {
          return x;
        }
        if (typeof x === 'string' && x.trim() !== '') {
          const n = Number(x);
          return Number.isFinite(n) ? n : x;
        }
        return x;
      });
    }
    return value.map((item) => walkBody(item, null));
  }
  if (!isPlainObject(value)) {
    return value;
  }

  const o = value;
  const out: Record<string, unknown> = {};

  for (const k of Object.keys(o)) {
    const v = o[k];

    if (GEO_KEYS.has(k)) {
      const n = coerceSingleGeoToPrimitive(v);
      if (n !== undefined) {
        out[k] = n;
      }
      continue;
    }

    if (k === 'facilities' && Array.isArray(v)) {
      out[k] = walkBody(v, 'facilities') as unknown;
      continue;
    }

    if (k === 'opening_times') {
      out[k] = coerceRootOpeningTimesValue(v);
      continue;
    }

    if (isPhoneWireFieldKey(k) && typeof v === 'string') {
      out[k] = normalizePhoneForWire(v);
      continue;
    }

    if (v === null) {
      out[k] = null;
      continue;
    }
    if (v === undefined) {
      continue;
    }

    if (isPlainObject(v)) {
      out[k] = walkBody(v, k);
      continue;
    }
    if (Array.isArray(v)) {
      out[k] = v.map((item) => (isPlainObject(item) ? walkBody(item, k) : item));
      continue;
    }
    if (ASSET_NUMERIC_KEYS.has(k)) {
      out[k] = coerceNonGeoNumericLeaf(v);
    } else {
      out[k] = v;
    }
  }

  return out;
}

/** @deprecated use prepareAssetRequestBody */
export function normalizeAssetRequestBody(body: unknown): unknown {
  return prepareAssetRequestBody(body);
}

/** @deprecated use prepareAssetRequestBody + stringifyPreparedAssetForHttp */
export function stringifyAssetRequestBody(body: unknown): string {
  return stringifyPreparedAssetForHttp(body);
}
